import { defineComponent as _defineComponent } from 'vue'
import CcCard from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCard.ts.vue';
import CcCardVariable from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCardVariable.ts.vue';
import CcIcon from '@/views/Automation/CampaignCreatorV2/components/CcIcon/CcIcon.ts.vue';
import CcPopover from '@/views/Automation/CampaignCreatorV2/components/CcPopover/CcPopover.ts.vue';
import { Icons } from '@/views/Automation/CampaignCreatorV2/helpers/icons';
import { useTour } from '@/views/Automation/CampaignCreatorV2/helpers/tourGuide';
import useCampaignStore from '@/views/Automation/CampaignCreatorV2/store/Campaign';
import { TreeselectOption } from '@/views/Automation/CampaignCreatorV2/types';
import { CampaignDTO } from '@/views/Automation/CampaignCreatorV2/validation/CampaignDTO';
import rulesConfig from '@/views/Automation/Rules/config';
import Treeselect from '@riophae/vue-treeselect';
import { apiStore } from '@sh/services/api';
import { PopoverTriggers, Position, TrafficSource } from '@sh/types';
import { Config, RuleType } from '@sh/views/Campaign/types';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

type TreeSelectRulesOption = TreeselectOption<{
  groups?: string[];
  action: string;
  ruleId: string;
}>;

interface TreeSelectRef {
  closeMenu: () => void;
  clear: () => void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PreviewPublish.ts',
  setup(__props) {

onMounted(() => {
  useTour('PublishAndAutomation');
});

const treeselectRulesRef = ref<NullableType<TreeSelectRef>>(null);
const rules = ref<RuleType[]>([]);
const rulesTreeselectOptions = ref<TreeSelectRulesOption[]>([]);

const campaignStore = useCampaignStore();
const campaignGetters = storeToRefs(campaignStore);
const selectedRules = computed(() => campaignGetters.getSelectedRules.value ?? []);
const allRulesAsOptions = computed(() => rulesTreeselectOptions.value.flatMap((option) => option.children ?? []));
const selectedRulesSelectValue = computed(() =>
  selectedRules.value
    .map((e) => allRulesAsOptions.value.filter((o) => o.ruleId === e))
    .filter(Boolean)
    .flat()
);
const campaignTasks = computed(() => {
  const { campaignTaskForCreation, updatedAt } = campaignGetters.getComputedCampaignForCreation.value;
  campaignTaskForCreation.forEach((e) => e.calculateName());
  console.log('Campaign that will be created', { campaignTaskForCreation, updatedAt });
  return campaignTaskForCreation;
});
const campaignCreatedList = computed(() => campaignTasks.value?.map((t) => t.getCampaignStructureTask()) ?? []);

const campaignMetrics = computed(() => {
  const taskMetrics = campaignTasks.value?.map((t) => t.getAllMetrics()).flat() ?? [];
  return CampaignDTO.reduceMetrics(taskMetrics);
});

function getRuleById(id: string) {
  const found = rules?.value?.find((rule) => rule._id === id)!;
  return {
    name: found?.name,
    label: found?.name,
    id: found?._id,
    action: found?.rule_action,
    groups: found?.group,
  };
}

function mapRulesToOptions(rules: RuleType[], ruleConfig: Config) {
  const ruleMap: Record<string, { key: string; content: string }> = {};
  for (const [key, item] of Object.entries(ruleConfig.ruleConfig.ruleTypes)) {
    if (item.ts_types.includes(TrafficSource.Facebook)) {
      ruleMap[`${item.level}:${(item as any).rule_action_alias || item.rule_action}`] = {
        key,
        content: item.content,
      };
    }
  }

  const facebookRules = rules.filter((item: RuleType) => !!ruleMap[`${item.level}:${item.rule_action}`]);
  const uniqueGroups = Array.from(new Set(facebookRules.flatMap((rule) => rule.group || [])));

  const groupOptions = uniqueGroups.map((group) => {
    const children = facebookRules
      .filter((rule) => rule.group && rule.group.includes(group))
      .map((rule) => {
        const { content } = ruleMap[`${rule.level}:${rule.rule_action}`];
        return {
          label: rule.name || 'Rule Name',
          action: rule.rule_action,
          description: content,
          id: `${group}-${rule._id}`,
          ruleId: rule._id,
          groups: rule.group,
        };
      });

    return {
      label: group,
      id: group,
      children,
    };
  });

  rulesTreeselectOptions.value = [
    {
      label: 'All Rules',
      id: 'all',
      children: facebookRules.map((rule) => {
        const { content } = ruleMap[`${rule.level}:${rule.rule_action}`];
        return {
          label: rule.name || 'Rule Name',
          id: rule._id,
          description: content,
          ruleId: rule._id,
          action: rule.rule_action,
        };
      }),
    },
    ...groupOptions,
  ];
}

async function getAllRules() {
  rulesTreeselectOptions.value = [{ id: 'loading', label: 'Loading...', isDisabled: true }];

  try {
    rules.value = await apiStore.rules.index();
    const ruleConfig = await rulesConfig.getConfig();
    mapRulesToOptions(rules.value, ruleConfig);
  } catch (error) {
    rulesTreeselectOptions.value = [];
    console.error(error);
  }
}

function onApplyRules(ruleOption: TreeSelectRulesOption) {
  const ruleIds = (ruleOption as any)?.ruleId
    ? [(ruleOption as any)?.ruleId]
    : ruleOption?.children?.map((e) => e.ruleId) || [].filter(Boolean);
  const uniqueRuleIds = Array.from(new Set([...selectedRules.value, ...ruleIds]));
  campaignStore.setRuleIds(uniqueRuleIds);
}

function onDeselectRules(ruleOption: TreeSelectRulesOption) {
  const ruleIds = (ruleOption as any)?.ruleId
    ? [(ruleOption as any)?.ruleId]
    : ruleOption?.children?.map((e) => e.ruleId) || [].filter(Boolean);
  const uniqueRuleIds = Array.from(new Set([...selectedRules.value.filter((e) => !ruleIds.includes(e))]));
  campaignStore.setRuleIds(uniqueRuleIds);
}

function onRemoveRule(id: string) {
  campaignStore.setRuleIds(selectedRules.value.filter((ruleId) => ruleId !== id));
}

function onClearRules() {
  campaignStore.setRuleIds([]);
}

function generateGroupsPreview(groups: string[] = []) {
  return groups.length ? `Groups: ${groups.join(', ')}` : 'No groups for this rule';
}

function generateRuleActionIcon(action: string | undefined): Icons {
  const actionIconMap: Record<string, Icons> = {
    start: 'playIconRule',
    pause: 'pauseIconRule',
    day_: 'calendarIconRule',
    change: 'dollarIconRule',
    coefficient: 'coefficientIconRule',
  };

  for (const [key, icon] of Object.entries(actionIconMap)) {
    if (action?.includes(key)) {
      return icon;
    }
  }

  return 'boltIcon';
}

getAllRules();

return { __sfc: true,treeselectRulesRef, rules, rulesTreeselectOptions, campaignStore, campaignGetters, selectedRules, allRulesAsOptions, selectedRulesSelectValue, campaignTasks, campaignCreatedList, campaignMetrics, getRuleById, mapRulesToOptions, getAllRules, onApplyRules, onDeselectRules, onRemoveRule, onClearRules, generateGroupsPreview, generateRuleActionIcon, CcCard, CcCardVariable, CcIcon, CcPopover, Treeselect, PopoverTriggers, Position }
}

})